import React from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Slider from 'react-slick';
import { observer } from 'mobx-react-lite';

import { getColor } from './UICommon';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useMst } from '../../models/rootModel';
import { Group } from '../../models/Group';

import { GroupCard } from './GroupCard';
import { QuoteCard, QuoteCardProps } from './QuoteCard';

const CarouselDiv = styled(Box)`
  padding-top: 8px;
  margin-bottom: 56px;
  & .slick-slide {
    height: auto;
  }
  & .slick-track {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: stretch;
  }
  & .slick-dots {
    bottom: -20px;
    & li {
      font-size: 12px;
      margin: 0;
      height: 8px;
      width: 8px;
      margin-right: 8px;
      & button {
        padding: 0;
        height: 8px;
        width: 8px;
        margin-right: 8px;
      }
    }
    & button::before {
      color: #d9d9d9;
      opacity: 1;
      height: 8px;
      width: 8px;
      font-size: 8px;
    }
    & li.slick-active {
      & button::before {
        color: ${getColor('PURPLE_100')};
        height: 8px;
        width: 8px;
        font-size: 8px;
      }
    }
  }
`;

const BottomDiv = styled.div`
  margin: 0 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid ${getColor('BLACK_10')};
`;

export enum CarouselType {
  Group = 'Group',
  Quote = 'Quote',
  Facilitator = 'Facilitator',
}

interface GroupsCarouselProps {
  listInformation?: QuoteCardProps[];
  type: CarouselType;
  isBottomBorder?: boolean;
  buttonText?: string;
  stepName: string;
  onClick?: (id: string) => void;
  isButtonDisabled?: boolean;
}

export const CarouselComponent = observer(
  ({
    listInformation,
    type,
    isBottomBorder,
    buttonText,
    stepName,
    onClick,
    isButtonDisabled = false,
  }: GroupsCarouselProps) => {
    const { Group } = useMst();

    const MEMBERS_SLIDER_SETTINGS = {
      centerMode: true,
      infinite: false,
      speed: 1000,
      arrows: false,
      swipeToSlide: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      dots: type === CarouselType.Facilitator ? false : true,
      responsive: [
        {
          breakpoint: 480,
          settings: {
            className: 'slider variable-width',
            variableWidth: true,
          },
        },
      ],
    };

    const RenderCard = ({ type, stepName }: { type: CarouselType; stepName: string }) => {
      switch (type) {
        case CarouselType.Group:
          return Group.groups.map((group: Group, index: number) => (
            <GroupCard
              id={`selectGroup_${index + 1}`}
              key={group.id}
              stepName={stepName}
              isButtonDisabled={isButtonDisabled}
              group={group}
              buttonText={buttonText}
              onClick={onClick}
              isCarousel={true}
            />
          ));
        case CarouselType.Quote:
          return listInformation?.map((quote: QuoteCardProps) => (
            <QuoteCard key={quote.quote} {...quote} isCarousel={true} />
          ));
        default:
          return <></>;
      }
    };

    return (
      <>
        <CarouselDiv>
          <Slider {...MEMBERS_SLIDER_SETTINGS}>{RenderCard({ type, stepName })}</Slider>
        </CarouselDiv>
        {isBottomBorder ? <BottomDiv /> : ''}
      </>
    );
  }
);
